/**
 * PROFILE (design/exports/account/web-3-profile): identity (avatar, name, sign-in provider, coin balance), the
 * ranked ladder card and stat tiles, reachable from Settings → ACCOUNT's "Signed in as" card. Only real data —
 * no touchdowns / best streak / season (this game doesn't track those; see `profile.ts`'s doc comment).
 */
import './profile.css';
import { useEffect } from 'preact/hooks';
import { BALL_SKIN_IDS, CHARACTERS, ballSkinCatalogFor } from '@hf/shared';
import { draft, onlineCharacter } from '@hf/client-core/ui-model/flow';
import { profileRank, profileStats } from '@hf/client-core/ui-model/profile';
import { thousands } from '@hf/client-core/ui-model/format';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import { useActions, useServices } from '../actions';
import { BallSkinArt, CoinBalance, Head } from '../components';
import { useFocusGrid } from '../nav';
import { equippedBallSkin, wallet, walletBalance, webAccount } from '../wallet';
import { Shell } from './Shell';

const netOnline = (s: string): boolean => s !== 'offline' && s !== 'error' && s !== 'connecting';

export function Profile() {
  const actions = useActions();
  const { audio, online } = useServices();
  const back = (): void => {
    playSfx(audio, UI_SFX.back);
    actions.goto('settings');
  };
  useFocusGrid({ count: 1, active: true, onBack: back });

  useEffect(() => {
    const fetchRating = (): void => {
      if (typeof online.requestRating === 'function') online.requestRating().catch(() => undefined);
    };
    if (!online.connected) online.connect().then(fetchRating).catch(() => undefined);
    else fetchRating();
  }, []);

  const net = ui.net.value;
  const rank = profileRank(net.rating);
  const stats = profileStats(net.rating, (id) => wallet.owns(id));
  const info = webAccount.value;
  const providers = info?.providers ?? [];
  const via = providers.includes('apple') ? 'Apple' : providers.includes('google') ? 'Google' : null;
  const headId = draft.value.characters[0] ?? onlineCharacter.value;
  const headName = (CHARACTERS.find((c) => c.id === headId)?.name ?? '').replace('THE ', '');
  const ballId = equippedBallSkin.value;
  const ballsOwned = BALL_SKIN_IDS.filter((id) => wallet.owns(id) || ballSkinCatalogFor(id).priceCoins === 0).length;
  const rankNote =
    rank.state === 'unknown' ? (netOnline(net.status) ? 'Play ranked to get rated' : 'Offline') : null;

  return (
    <Shell screen="profile" title="PROFILE" onBack={back} stadium="night" hints={[['back', 'BACK']]}>
      <div class="hf-profile-card hf-panel">
        <div class="hf-profile-id">
          <Head character={onlineCharacter.value} expression="determined" class="hf-profile-id__head" />
          <div class="hf-profile-id__who">
            <small class="t-broadcast">PLAYER</small>
            <strong class="t-display">{info?.name ?? 'Player'}</strong>
            {via ? <span class="hf-profile-id__via">via {via}</span> : null}
          </div>
          <CoinBalance balance={walletBalance.value} class="hf-profile-id__coins" />
        </div>

        <div class="hf-profile-rank">
          <div class="hf-profile-rank__icon" aria-hidden="true">
            🏆
          </div>
          <div class="hf-profile-rank__label">
            <small class="t-broadcast">{rank.state === 'ranked' ? 'RANKED' : ''}</small>
            <strong class="t-display">
              {rank.state === 'ranked' ? rank.tier : rank.state === 'unranked' ? 'UNRANKED' : '—'}
            </strong>
            {rankNote ? <span class="hf-profile-rank__note">{rankNote}</span> : null}
          </div>
          <div class="hf-profile-rank__elo">
            <b class="t-display">{rank.state === 'unknown' ? '—' : thousands(rank.elo)}</b>
            <small class="t-broadcast">ELO</small>
          </div>
        </div>

        <div class="hf-profile-stats">
          <StatTile label="MATCHES" value={stats.matches} />
          <StatTile label="WINS" value={stats.wins} />
          <StatTile label="WIN RATE" value={stats.winRatePct !== null ? `${stats.winRatePct}%` : null} />
          <StatTile label="LOSSES" value={stats.losses} />
          <StatTile label="HEADS" value={`${stats.headsOwned}/${stats.headsTotal}`} />
        </div>

        <div class="hf-profile-equipped" data-testid="profile-equipped">
          <small class="t-broadcast hf-profile-equipped__title">EQUIPPED</small>
          <div class="hf-profile-equipped__item">
            <Head character={headId} class="hf-profile-equipped__art" />
            <div>
              <small class="t-broadcast">HEAD</small>
              <strong class="t-display">{headName}</strong>
            </div>
          </div>
          <div class="hf-profile-equipped__item">
            <BallSkinArt skin={ballId} class="hf-profile-equipped__art" />
            <div>
              <small class="t-broadcast">BALL</small>
              <strong class="t-display">{ballSkinCatalogFor(ballId).name}</strong>
            </div>
          </div>
          <span class="hf-profile-equipped__count">
            {ballsOwned}/{BALL_SKIN_IDS.length} BALLS
          </span>
        </div>

        <p class="hf-profile-fair">
          🛡 Fair play: coins only unlock heads &amp; balls. Elo is earned on the field, never bought.
        </p>
      </div>
    </Shell>
  );
}

function StatTile({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div class="hf-profile-tile">
      <b class="t-display">{value === null ? '—' : value}</b>
      <small class="t-broadcast">{label}</small>
    </div>
  );
}
